/**
 * OutcomePage Component
 * Displays assessment outcome and personalized recommendations
 */

import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Alert,
  CircularProgress,
  Typography,
} from '@mui/material';
import AppLayout from '../components/layouts/AppLayout';
import { isEmbedded } from '../utils/authHelper';
import { useEmbedResize } from '../hooks/useEmbedResize';
import OutcomeCard from '../components/outcome/OutcomeCard';
import apiClient from '../api/client';
import { ASSESSMENT_ENDPOINTS } from '../api/endpoints';
import type { Assessment, OutcomeTemplate, ScoringConfig, OutcomeTierDef, Question } from '../types';

interface OutcomeNavState {
  assessment?: Assessment;
  scoringConfig?: ScoringConfig;
  questions?: Question[];
  quizName?: string;
  quizDescription?: string;
  showQuizName?: boolean;
  showQuizDescription?: boolean;
}

interface OutcomeState {
  assessment: Assessment | null;
  template: OutcomeTemplate | null;
  tierDef: OutcomeTierDef | null;
  scoringConfig: ScoringConfig | null;
  isLoading: boolean;
  error: string | null;
}

function OutcomePage(): JSX.Element {
  const { assessmentId = '' } = useParams<{ assessmentId?: string }>();
  const location = useLocation();
  useEmbedResize();
  const navState = location.state as OutcomeNavState | null;
  const passedAssessment = navState?.assessment;
  const passedScoringConfig = navState?.scoringConfig;
  // eslint-disable-next-line no-console
  console.log('[CIJ] Outcome navigation state', {
    assessmentId,
    hasNavigationState: Boolean(navState),
    hasAssessment: Boolean(passedAssessment),
    hasScoringConfig: Boolean(passedScoringConfig),
    hasCijIntegration: Boolean(passedScoringConfig?.cijIntegration),
    cijEnabled: passedScoringConfig?.cijIntegration?.enabled ?? false,
    fieldMappingCount: passedScoringConfig?.cijIntegration?.fieldMappings?.length ?? 0,
  });
  const showQuizName = navState?.showQuizName ?? true;
  const showQuizDescription = navState?.showQuizDescription ?? true;
  const embedded = isEmbedded();
  const wrap = (content: React.ReactNode): JSX.Element =>
    embedded ? <>{content}</> : <AppLayout showNavigation={false}>{content}</AppLayout>;

  const [state, setState] = useState<OutcomeState>({
    assessment: null,
    template: null,
    tierDef: null,
    scoringConfig: null,
    isLoading: true,
    error: null,
  });

  React.useEffect(() => {
    // eslint-disable-next-line no-console
    console.warn('[CIJ] Outcome page mounted', {
      assessmentId,
      loading: state.isLoading,
      error: state.error,
      hasAssessment: Boolean(state.assessment),
      hasTemplate: Boolean(state.template),
      hasScoringConfig: Boolean(state.scoringConfig),
      hasCijIntegration: Boolean(state.scoringConfig?.cijIntegration),
      cijEnabled: state.scoringConfig?.cijIntegration?.enabled ?? false,
    });
  }, [assessmentId, state.assessment, state.error, state.isLoading, state.scoringConfig, state.template]);

  // Load assessment outcome
  useEffect(() => {
    const loadAssessment = async (): Promise<void> => {
      if (!assessmentId) {
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: 'Invalid assessment ID',
        }));
        return;
      }

      try {
        // Use passed assessment data if available; otherwise fetch
        const assessment: Assessment = passedAssessment ?? (
          await apiClient.get<Assessment>(ASSESSMENT_ENDPOINTS.GET(assessmentId))
        ).data;

        const scoringConfig: ScoringConfig | undefined = passedScoringConfig;
        const matchedTier = scoringConfig?.outcomeTiers?.find(
          (t) => t.name === assessment.outcomeTier,
        );

        const mockTemplate: OutcomeTemplate = {
          id: 1,
          tier: assessment.outcomeTier,
          // fall back to description for records saved before field was renamed
          message: matchedTier?.message ?? (matchedTier as any)?.description ?? `Your assessment outcome: ${assessment.outcomeTier}`,
          recommendation: matchedTier?.recommendation ?? '',
          cta: matchedTier?.ctaText,
          ctaUrl: matchedTier?.ctaUrl,
        };

        setState((prev) => ({
          ...prev,
          assessment,
          template: mockTemplate,
          tierDef: matchedTier ?? null,
          scoringConfig: scoringConfig ?? null,
          isLoading: false,
        }));
      } catch (error) {
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: 'Failed to load assessment outcome. Please try again.',
        }));
      }
    };

    loadAssessment();
  }, [assessmentId]);

  if (state.isLoading) {
    return wrap(
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (state.error || !state.assessment || !state.template) {
    return wrap(
      <Container maxWidth="md">
        <Alert severity="error">{state.error || 'Assessment not found'}</Alert>
      </Container>
    );
  }

  return wrap(
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
          {showQuizName && navState?.quizName && (
            <Box sx={{ mb: 4 }}>
              <Typography
                sx={{
                  display: 'block',
                  mb: 1,
                  fontSize: '0.85rem',
                  fontWeight: 700,
                  color: '#00A9B5',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                }}
              >
                {navState.quizName}
              </Typography>
              {showQuizDescription && navState?.quizDescription && (
                <Typography variant="body1" color="textSecondary">{navState.quizDescription}</Typography>
              )}
            </Box>
          )}

          {!showQuizName && showQuizDescription && navState?.quizDescription && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="body1" color="textSecondary">{navState.quizDescription}</Typography>
            </Box>
          )}

          <OutcomeCard
            tier={state.assessment.outcomeTier}
            template={state.template}
            tierDef={state.tierDef ?? undefined}
            cijIntegration={state.scoringConfig?.cijIntegration}
            responses={state.assessment.responses}
            questions={navState?.questions}
            prospectEmail={state.assessment.prospectEmail}
          />
        </Box>
      </Container>
  );
}

export default OutcomePage;
